import { Edit3, Globe, MessageCircle, Pin, Trash, User, Users } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'

import { Badge } from '@/components/ui/badge'
import { Tabs } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import { GroupAvatar } from './group-avatar'

const ONLINE_WINDOW_MS = 5 * 60 * 1000

interface MobileConversationListProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  conversations: any[]
  selectedConversationId: number | undefined
  unreadCounts: Record<number, number>
  isLoading: boolean
  onSelectConversation: (conversationId: number) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEditConversationName: (conversation: any) => void
  onDeleteConversation: (conversationId: number) => void
  onPinConversation: (conversationId: number) => void
  onUnpinConversation: (conversationId: number) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getConversationTitle: (conversation: any) => string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTargetedUser: (conversation: any) => any
  formatTimestamp: (timestamp: string) => string
  targetUserAvatar?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  authUser?: any
  conversationListRef?: React.RefObject<HTMLDivElement>
}

export const MobileConversationList: React.FC<MobileConversationListProps> = ({
  conversations,
  selectedConversationId,
  unreadCounts,
  isLoading,
  onSelectConversation,
  onEditConversationName,
  onDeleteConversation,
  onPinConversation,
  onUnpinConversation,
  getConversationTitle,
  getTargetedUser,
  formatTimestamp,
  targetUserAvatar,
  authUser,
  conversationListRef,
}) => {
  const isDirectConversation = React.useCallback((conversation: { type?: string; participants?: unknown[] }) => {
    if (!conversation) return false
    if (conversation.type === 'direct') return true
    if (conversation.type === 'group' || conversation.type === 'course' || conversation.type === 'private_group' || conversation.type === 'community') {
      return false
    }
    return Array.isArray(conversation.participants) && conversation.participants.length === 2
  }, [])

  // State to track the current active tab
  const [currentTab, setCurrentTab] = React.useState<'direct' | 'group' | 'community'>('direct')
  const [isManualTabChange, setIsManualTabChange] = React.useState(false)

  // Helper function to render a conversation item
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderConversationItem = (conv: any) => (
    <div
      key={conv.id}
      data-conversation-id={conv.id}
      onClick={() => onSelectConversation(conv.id)}
      className={`p-4 rounded-xl cursor-pointer transition-all duration-200 ${
        selectedConversationId === conv.id
          ? 'bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 shadow-sm'
          : 'hover:bg-gray-50 hover:shadow-sm border border-transparent'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {(() => {
                // For group, course, and community chats, use GroupAvatar component
                if (
                  conv.type === 'group' ||
                  conv.type === 'course' ||
                  conv.type === 'private_group' ||
                  conv.type === 'community'
                ) {
                  const activeParticipants =
                    conv.participants?.filter(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (p: any) => p.status === 'active' && p.user && p.user.id !== authUser?.id
                    ) || []
                  return <GroupAvatar participants={activeParticipants} size="lg" conversationAvatar={conv.avatar} />
                }

                // For direct chats, use single user avatar
                const targetedUser = getTargetedUser(conv)
                if (!targetedUser) {
                  if (targetUserAvatar) {
                    return (
                      <Image
                        src={targetUserAvatar}
                        alt="Target user avatar"
                        width={40}
                        height={40}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    )
                  } else {
                    return (
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                        <User className="w-5 w-5 text-gray-600" />
                      </div>
                    )
                  }
                }
                return (
                  <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                    {targetedUser.user.avatar ? (
                      <Image
                        src={targetedUser.user.avatar}
                        alt={`${targetedUser.user.firstName} ${targetedUser.user.lastName}`}
                        width={40}
                        height={40}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <User className="w-5 w-5 text-gray-600" />
                    )}
                  </div>
                )
              })()}
            </div>

            {/* Conversation Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <TooltipProvider>
                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <div className="flex min-w-0 items-center gap-1">
                          <h3 className="truncate text-sm font-medium text-gray-900 cursor-default">
                            {getConversationTitle(conv)}
                          </h3>
                          {conv.participants?.some((participant: { user: { id: number; lastActiveAt?: string | null } | null }) => {
                            if (!participant.user || participant.user.id === authUser?.id || !participant.user.lastActiveAt) {
                              return false
                            }
                            const lastActiveAtMs = new Date(participant.user.lastActiveAt).getTime()
                            if (Number.isNaN(lastActiveAtMs)) {
                              return false
                            }
                            return Date.now() - lastActiveAtMs <= ONLINE_WINDOW_MS
                          }) && (
                            <span
                              className="h-2 w-2 flex-shrink-0 rounded-full bg-green-500"
                              title="Someone is online"
                              aria-label="Someone is online"
                            />
                          )}
                          {conv.isPinned && <Pin className="h-3 w-3 text-blue-500 fill-current flex-shrink-0" />}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-sm">{getConversationTitle(conv)}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="ml-2 flex flex-shrink-0 flex-col items-end gap-1">
                  {/* Unread Count */}
                  {unreadCounts[conv.id] > 0 && (
                    <Badge className="text-xs bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0 shadow-sm">
                      {unreadCounts[conv.id]}
                    </Badge>
                  )}
                  <span className="text-xs text-gray-500">
                    {conv.lastMessageAt ? formatTimestamp(conv.lastMessageAt) : formatTimestamp(conv.updatedAt)}
                  </span>
                </div>
              </div>
              {/* Display last message content */}
              {conv.lastMessageContent && (
                <p
                  className="text-xs text-gray-600 truncate mt-1"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(conv.lastMessageContent) }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-gray-100">
        {/* Pin/Unpin button */}
        {conv.isPinned ? (
          <button
            onClick={e => {
              e.stopPropagation()
              onUnpinConversation(conv.id)
            }}
            className="p-2 text-blue-600 hover:text-blue-700 rounded-lg hover:bg-blue-50"
          >
            <Pin className="w-4 h-4 fill-current" />
          </button>
        ) : (
          <button
            onClick={e => {
              e.stopPropagation()
              onPinConversation(conv.id)
            }}
            className="p-2 text-gray-600 hover:text-blue-600 rounded-lg hover:bg-blue-50"
          >
            <Pin className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={e => {
            e.stopPropagation()
            onEditConversationName(conv)
          }}
          className="p-2 text-gray-600 hover:text-blue-600 rounded-lg hover:bg-blue-50"
        >
          <Edit3 className="w-4 h-4" />
        </button>
        <button
          onClick={e => {
            e.stopPropagation()
            onDeleteConversation(conv.id)
          }}
          className="p-2 text-gray-600 hover:text-red-600 rounded-lg hover:bg-red-50"
        >
          <Trash className="w-4 h-4" />
        </button>
      </div>
    </div>
  )

  // Get filtered conversations by type
  const groupConversations = conversations.filter(
    conv => conv.type === 'group' || conv.type === 'course' || conv.type === 'private_group'
  )
  const directConversations = conversations.filter(conv => isDirectConversation(conv))
  const communityConversations = conversations.filter(conv => conv.type === 'community')

  // Helper functions to calculate unread counts for each tab
  const calculateDirectUnreadCount = (): number => {
    return directConversations.reduce((total, conv) => total + (unreadCounts[conv.id] || 0), 0)
  }

  const calculateGroupUnreadCount = (): number => {
    return groupConversations.reduce((total, conv) => total + (unreadCounts[conv.id] || 0), 0)
  }

  const calculateCommunityUnreadCount = (): number => {
    return communityConversations.reduce((total, conv) => total + (unreadCounts[conv.id] || 0), 0)
  }

  // Determine initial tab based on selected conversation type
  React.useEffect(() => {
    if (selectedConversationId && conversations.length > 0 && !isManualTabChange) {
      const selectedConversation = conversations.find(conv => conv.id === selectedConversationId)
      if (selectedConversation) {
        // Switch to the appropriate tab based on conversation type
        if (
          selectedConversation.type === 'group' ||
          selectedConversation.type === 'course' ||
          selectedConversation.type === 'private_group'
        ) {
          setCurrentTab('group')
        } else if (isDirectConversation(selectedConversation)) {
          setCurrentTab('direct')
        } else if (selectedConversation.type === 'community') {
          setCurrentTab('community')
        }
      }
    }
  }, [selectedConversationId, conversations, isManualTabChange, isDirectConversation])

  // Reset manual tab change flag when selected conversation changes
  React.useEffect(() => {
    setIsManualTabChange(false)
  }, [selectedConversationId])

  // Handle manual tab change
  const handleTabChange = (value: string) => {
    setIsManualTabChange(true)
    setCurrentTab(value as 'direct' | 'group' | 'community')
  }

  const tabItems: Array<{
    key: 'direct' | 'group' | 'community'
    label: string
    Icon: React.ComponentType<{ className?: string }>
    count: number
  }> = [
    { key: 'direct', label: 'Direct', Icon: MessageCircle, count: calculateDirectUnreadCount() },
    { key: 'group', label: 'Groups', Icon: Users, count: calculateGroupUnreadCount() },
    { key: 'community', label: 'Community', Icon: Globe, count: calculateCommunityUnreadCount() },
  ]

  return (
    <div className="flex flex-col h-full max-h-full">
      {/* Conversations List */}
      <div ref={conversationListRef} className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <div className="space-y-3 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : conversations.length > 0 ? (
          <Tabs value={currentTab} onValueChange={value => handleTabChange(value)} className="w-full">
            <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b border-gray-100 px-2">
              <div className="relative flex items-stretch">
                {tabItems.map(({ key, label, Icon, count }) => {
                  const isActive = currentTab === key
                  return (
                    <button
                      key={key}
                      data-tab={key}
                      onClick={() => handleTabChange(key)}
                      className={`group relative flex flex-1 items-center justify-center gap-1.5 px-3 py-3 text-xs font-medium transition-colors duration-200 ${
                        isActive ? 'text-blue-600' : 'text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${
                          isActive ? 'scale-110' : 'group-hover:scale-105'
                        }`}
                      />
                      <span className="tracking-tight">{label}</span>
                      {count > 0 && (
                        <Badge
                          variant="secondary"
                          className={`ml-0.5 h-4 min-w-[1rem] rounded-full border-0 px-1.5 text-[10px] font-semibold shadow-sm ${
                            isActive ? 'bg-blue-600 text-white' : 'bg-red-500 text-white'
                          }`}
                        >
                          {count}
                        </Badge>
                      )}
                      <span
                        className={`pointer-events-none absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-blue-600 transition-all duration-300 ${
                          isActive ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'
                        }`}
                      />
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Tab Content */}
            {currentTab === 'group' && (
              <div className="space-y-1 p-2">
                {groupConversations.length > 0 ? (
                  groupConversations.map(renderConversationItem)
                ) : (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-700 mb-1">No group chats yet</p>
                    <p className="text-xs text-gray-500">Create a group chat to start collaborating</p>
                  </div>
                )}
              </div>
            )}

            {currentTab === 'direct' && (
              <div className="space-y-1 p-2">
                {directConversations.length > 0 ? (
                  directConversations.map(renderConversationItem)
                ) : (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <MessageCircle className="w-6 h-6 text-blue-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-700 mb-1">No direct messages yet</p>
                    <p className="text-xs text-gray-500">Start a conversation with someone</p>
                  </div>
                )}
              </div>
            )}

            {currentTab === 'community' && (
              <div className="space-y-1 p-2">
                {communityConversations.length > 0 ? (
                  communityConversations.map(renderConversationItem)
                ) : (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Globe className="w-6 h-6 text-blue-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-700 mb-1">No community chats yet</p>
                    <p className="text-xs text-gray-500">Create a community to start collaborating</p>
                  </div>
                )}
              </div>
            )}
          </Tabs>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <MessageCircle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-base font-medium">No conversations yet</p>
            <p className="text-sm mt-2">Start a new conversation to begin chatting</p>
          </div>
        )}
      </div>
    </div>
  )
}
